import * as React from 'react'
import { CaretRight } from '@phosphor-icons/react'
import { cn } from '../../lib/utils'
import { AddPill } from './add-pill'
import { ColorDot } from './color-dot'
import { SearchBar } from './search-bar'
import type { BadgeColor } from './badge'

/**
 * Tree — a lightweight recursive hierarchy for sidebar/detail views: each node
 * has an optional icon, a label, an optional subtitle, and optional children.
 * Nodes with children get a caret toggle; a connector line threads each level.
 *
 * Built for entity-detail sidebars (related companies, products → variants →
 * SKUs, contacts). Labels/subtitles accept ReactNode, so callers pass links,
 * badges, or styled text.
 *
 * Expansion is uncontrolled per node — seed it with `defaultExpanded` on the
 * node (or the Tree-wide `defaultExpanded`). Pass `aria-label` describing the
 * list.
 *
 * Semantics are a nested `role="list"` / `role="listitem"` — deliberately NOT
 * the ARIA `tree` pattern. This is a disclosure list of records whose
 * interactivity lives in each row's own links / caret / action buttons (all
 * individually tab-focusable), not a single-tab-stop, arrow-key-navigable tree.
 * A future navigational surface (a nav menu, a file browser) that needs roving
 * focus should use a dedicated component built on the react-aria tree pattern,
 * rather than widening this one.
 *
 * Long labels truncate (they don't wrap) so rows keep their rhythm in a
 * fixed-width panel. Rows highlight on hover; the caret is a ≥24px hit target.
 *
 * Per-node `actions` (edit / remove / …) overlay the row's right edge and
 * reveal on hover or keyboard focus — wire them to open a dialog. Pass
 * `selectedId` to mark the active node (`aria-current` + a neutral fill).
 *
 * `collapseAfter={n}` truncates each list (root + every group) to n rows with a
 * "View N more" / "Show less" toggle. `onAdd` + `addLabel` append a dashed
 * "+ Add …" pill button at the end of the root list. For very long lists, wrap
 * the Tree in a scroll container (`max-h-* overflow-auto fbui-scroll`).
 *
 * When the root list is empty, `emptyState` renders a muted placeholder in its
 * place (the "+ Add …" pill still shows below it, so an empty section keeps its
 * add affordance). This is distinct from the filter's "No matches" state.
 *
 * For colored, searchable lists (tags, campaigns), give a node a `color`
 * (rendered as a leading ColorDot) and set `searchThreshold={n}` — once the
 * list exceeds n rows a filter field appears, matching on the label text (or a
 * node's `searchText` when the label isn't plain text).
 */

const ROW_GAP = 'gap-1'
const LABEL_CLASS = 'text-sm leading-snug'
// Height ≈ the label's first line, so a leading icon/dot centered inside it sits
// centered on that line (robust for any glyph, vs a fixed top margin).
const LINE_BOX = 'h-5'

export interface TreeNode {
  /** Unique id (used as React key). */
  id: string
  /** Primary label — ReactNode so it can be a link, badge, or styled text. */
  label: React.ReactNode
  /** Secondary line beneath the label (smaller, muted). */
  subtitle?: React.ReactNode
  /** Leading glyph (e.g. a Phosphor icon). Ignored if `color` is set. */
  icon?: React.ReactNode
  /** Palette color → renders a leading ColorDot (tags, campaigns). */
  color?: BadgeColor
  /** Text used for filtering when the label isn't a plain string. */
  searchText?: string
  /** Child nodes — their presence adds the expand/collapse toggle. */
  children?: TreeNode[]
  /** Whether this node starts expanded (falls back to the Tree's default). */
  defaultExpanded?: boolean
  /**
   * Row actions (e.g. edit / remove icon buttons) that overlay the right edge
   * and reveal on hover / keyboard focus. Wire them to open a dialog.
   */
  actions?: React.ReactNode
}

export interface TreeProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Root-level nodes. */
  nodes: TreeNode[]
  /** Id of the active node — highlights it (`aria-current` + neutral fill). */
  selectedId?: string
  /** Truncate each list to N rows with a "View N more" / "Show less" toggle. */
  collapseAfter?: number
  /** When set, appends a dashed "+ Add …" pill button at the end of the root list. */
  onAdd?: () => void
  /** Label for the add row (default "Add item"). */
  addLabel?: React.ReactNode
  /** Once the root list exceeds this many rows, show a filter field. */
  searchThreshold?: number
  /** Placeholder for the filter field (default "Search"). */
  searchPlaceholder?: string
  /** Muted placeholder shown when the root list is empty (e.g. "No deals yet"). */
  emptyState?: React.ReactNode
  /** Default expanded state for nodes that don't set their own. Default true. */
  defaultExpanded?: boolean
}

interface ListContext {
  selectedId?: string
  collapseAfter?: number
  fallbackExpanded: boolean
  /** True while a filter query is active — force-expands + skips collapsing. */
  searching: boolean
  /**
   * Whether this tree reserves the leading caret column. Decided once per tree:
   * true if any root node can expand (→ keep the column at every level so
   * nested leaves align under their parent). A fully-flat tree (tags/campaigns/
   * deals) is false, so its rows sit flush-left with the section header.
   */
  reserveToggle: boolean
}

/** Node text used for filtering — explicit `searchText`, else a string label. */
function nodeText(node: TreeNode): string {
  return (
    node.searchText ?? (typeof node.label === 'string' ? node.label : '')
  ).toLowerCase()
}

/**
 * Recursively filter to nodes matching `query`: a node is kept if it matches
 * (with all its children) or if any descendant matches (pruned to the matching
 * branch). Empty query returns the list unchanged.
 */
function filterTree(nodes: TreeNode[], query: string): TreeNode[] {
  const q = query.trim().toLowerCase()
  if (!q) return nodes
  const out: TreeNode[] = []
  for (const node of nodes) {
    if (nodeText(node).includes(q)) {
      out.push(node)
    } else if (node.children?.length) {
      const kids = filterTree(node.children, q)
      if (kids.length) out.push({ ...node, children: kids })
    }
  }
  return out
}

export const Tree = React.forwardRef<HTMLDivElement, TreeProps>(
  (
    {
      className,
      nodes,
      selectedId,
      collapseAfter,
      onAdd,
      addLabel,
      searchThreshold,
      searchPlaceholder = 'Search',
      emptyState,
      defaultExpanded = true,
      // Pulled off `props` so the accessible name lands on the inner
      // `role="list"` element, not the outer chrome container.
      'aria-label': ariaLabel,
      'aria-labelledby': ariaLabelledby,
      ...props
    },
    ref
  ) => {
    const [query, setQuery] = React.useState('')
    const showSearch =
      searchThreshold != null && nodes.length > searchThreshold
    const searching = showSearch && query.trim().length > 0
    const visibleNodes = searching ? filterTree(nodes, query) : nodes
    // Per-tree: reserve the caret column iff anything can expand. (Children only
    // exist under an expandable node, so a root-level check covers all depths.)
    const reserveToggle = nodes.some((n) => !!n.children?.length)

    return (
      // Outer container is presentational chrome (search field + empty message)
      // — NOT the list itself, so those non-item elements don't sit inside a
      // `role="list"`. The accessible name + list role live on the inner wrapper
      // that owns only `role="listitem"` rows.
      <div
        ref={ref}
        data-slot="tree"
        className={cn('flex flex-col', ROW_GAP, className)}
        {...props}
      >
        {showSearch && (
          <SearchBar
            size="sm"
            value={query}
            onValueChange={setQuery}
            onClear={() => setQuery('')}
            clearable
            placeholder={searchPlaceholder}
            aria-label={searchPlaceholder}
            // spacing below the field goes on the shell — `className` on
            // SearchBar targets the <input>, which would misalign it
            containerClassName="mb-1"
          />
        )}
        {searching && visibleNodes.length === 0 ? (
          <p role="status" className="py-1 text-sm text-fbui-gray-500">
            No matches
          </p>
        ) : (
          <>
            {!searching && nodes.length === 0 && emptyState != null && (
              <p className="py-1 text-sm text-fbui-gray-500">{emptyState}</p>
            )}
            {(visibleNodes.length > 0 || onAdd) && (
              <div
                role="list"
                aria-label={ariaLabel}
                aria-labelledby={ariaLabelledby}
                className={cn('flex flex-col', ROW_GAP)}
              >
                <NodeList
                  nodes={visibleNodes}
                  ctx={{
                    selectedId,
                    collapseAfter,
                    fallbackExpanded: defaultExpanded,
                    searching,
                    reserveToggle,
                  }}
                  onAdd={onAdd}
                  addLabel={addLabel}
                />
              </div>
            )}
          </>
        )}
      </div>
    )
  }
)
Tree.displayName = 'Tree'

/**
 * Renders a list of sibling nodes with optional "View N more" collapsing and a
 * trailing "+ Add" row. Used for both the root list and every child group, so
 * the behavior is identical at every level. Its children are emitted into the
 * parent flex column, so the parent's gap governs row spacing.
 */
function NodeList({
  nodes,
  ctx,
  onAdd,
  addLabel,
}: {
  nodes: TreeNode[]
  ctx: ListContext
  onAdd?: () => void
  addLabel?: React.ReactNode
}) {
  const [showAll, setShowAll] = React.useState(false)
  const { collapseAfter } = ctx
  const overflowing =
    !ctx.searching &&
    collapseAfter != null &&
    collapseAfter > 0 &&
    nodes.length > collapseAfter
  const visible = overflowing && !showAll ? nodes.slice(0, collapseAfter) : nodes
  const hiddenCount = overflowing ? nodes.length - (collapseAfter as number) : 0

  return (
    <>
      {visible.map((node) => (
        <TreeItem key={node.id} node={node} ctx={ctx} />
      ))}
      {/* Footer controls are wrapped as listitems so the enclosing role="list"
          owns only listitem children (the button keeps its own button role). */}
      {overflowing && (
        <div role="listitem">
          <button
            type="button"
            onClick={() => setShowAll((v) => !v)}
            // link-style hover (text-shade shift), not a row background — this is
            // a text control, not a content row.
            className="-mx-2 flex w-[calc(100%+1rem)] items-center gap-1.5 rounded-md px-2 text-left text-fbui-gray-500 transition-colors hover:text-fbui-gray-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-fbui-ring-brand"
          >
            {ctx.reserveToggle && (
              <span aria-hidden="true" className="-ml-1 w-6 shrink-0" />
            )}
            <span className={cn('font-medium', LABEL_CLASS)}>
              {showAll ? 'Show less' : `View ${hiddenCount} more`}
            </span>
          </button>
        </div>
      )}
      {onAdd && (
        <div role="listitem">
          {/* Dashed "+ Add" pill — a distinct affordance from the record rows
              (breaks the same-size-text rhythm, reads as a button). `mt-1` sets
              it a touch apart from the last row. */}
          <AddPill onClick={onAdd} className="mt-1">
            {addLabel ?? 'Add item'}
          </AddPill>
        </div>
      )}
    </>
  )
}

function TreeItem({ node, ctx }: { node: TreeNode; ctx: ListContext }) {
  const { selectedId, fallbackExpanded } = ctx
  const hasChildren = !!node.children?.length
  const selected = selectedId != null && node.id === selectedId
  const [expanded, setExpanded] = React.useState(
    node.defaultExpanded ?? fallbackExpanded
  )
  // Force branches open while filtering so matches are visible.
  const isExpanded = ctx.searching || expanded

  return (
    <div
      role="listitem"
      // Mark the active record for AT (listitem doesn't support aria-selected;
      // aria-current is the list-semantics equivalent). Disclosure state lives
      // on the caret button (its aria-expanded), where the control actually is.
      aria-current={selected || undefined}
    >
      <div
        className={cn(
          // named group ("treerow") so the hover-reveal actions scope to THIS
          // row — an unnamed `group` collides with any ancestor `.group` (e.g.
          // the Collapsible section wrapper), revealing every row's actions.
          'group/treerow relative -mx-2 flex items-start gap-1.5 rounded-md px-2 transition-colors hover:bg-fbui-gray-50',
          selected && 'bg-fbui-gray-100 hover:bg-fbui-gray-100'
        )}
      >
        {hasChildren ? (
          <button
            type="button"
            onClick={() => setExpanded((e) => !e)}
            // While filtering, branches are force-expanded (isExpanded above), so
            // disable the toggle — otherwise a click mutates the hidden `expanded`
            // with no on-screen change, then silently flips the node when the
            // query clears.
            disabled={ctx.searching}
            aria-label={isExpanded ? 'Collapse' : 'Expand'}
            aria-expanded={isExpanded}
            // 24px hit target (WCAG 2.5.8); -mt-0.5 optically centers the caret
            // on the label's first line, -ml-1 tucks the box toward the edge.
            className="-ml-1 -mt-0.5 flex size-6 shrink-0 items-center justify-center rounded text-fbui-gray-500 transition-colors hover:bg-fbui-gray-100 hover:text-fbui-gray-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-fbui-ring-brand disabled:cursor-default disabled:hover:bg-transparent"
          >
            <CaretRight
              weight="bold"
              className={cn(
                'size-3 transition-transform',
                isExpanded && 'rotate-90'
              )}
            />
          </button>
        ) : ctx.reserveToggle ? (
          <span aria-hidden="true" className="-ml-1 w-6 shrink-0" />
        ) : null}

        {node.color != null ? (
          <span
            aria-hidden="true"
            className={cn('flex shrink-0 items-center', LINE_BOX)}
          >
            <ColorDot color={node.color} />
          </span>
        ) : (
          node.icon && (
            <span
              aria-hidden="true"
              className={cn(
                'flex shrink-0 items-center text-fbui-gray-500 [&>svg]:size-3.5',
                LINE_BOX
              )}
            >
              {node.icon}
            </span>
          )
        )}

        <div className="min-w-0 flex-1">
          <div
            className={cn('truncate font-medium text-fbui-gray-900', LABEL_CLASS)}
          >
            {node.label}
          </div>
          {node.subtitle != null && (
            <p className="truncate text-xs text-fbui-gray-500">
              {node.subtitle}
            </p>
          )}
        </div>

        {node.actions && (
          <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center opacity-0 transition-opacity focus-within:pointer-events-auto focus-within:opacity-100 group-hover/treerow:pointer-events-auto group-hover/treerow:opacity-100">
            {/* gradient mask so the buttons don't sit on top of the label — the
                `from-*` matches the row bg (gray-100 selected, else gray-50 hover)
                so there's no seam behind the buttons. */}
            <div
              className={cn(
                'flex items-center gap-0.5 bg-gradient-to-l from-70% to-transparent pl-8 pr-2',
                selected ? 'from-fbui-gray-100' : 'from-fbui-gray-50'
              )}
            >
              {node.actions}
            </div>
          </div>
        )}
      </div>

      {hasChildren && isExpanded && (
        <div
          role="list"
          // ml-2 centers the connector line under the 24px caret toggle.
          className={cn(
            'ml-2 mt-1 flex flex-col border-l border-fbui-gray-200 pl-3',
            ROW_GAP
          )}
        >
          <NodeList nodes={node.children!} ctx={ctx} />
        </div>
      )}
    </div>
  )
}
